import { FileData } from "@/utils/types";
import { useState } from "react";
import FileIcons from "@/utils/seti-ui-icons.json";
import { ChevronDown, ChevronRightIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import useDesignSystemStore from "@/store/zustand/aiDesignSystemBuilder";
import React from "react";

// Recursive component to render file/folder tree
const FileTree: React.FC<{
  fileData: FileData;
  onDelete: (filePath: string) => void;
}> = React.memo(({ fileData, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const filename = fileData.fullPath.split("/").pop();
  const ext = filename?.split(".").pop();

  // @ts-expect-error TS2538
  const iconFilename = FileIcons[filename];
  // @ts-expect-error TS2538
  const iconForExt = FileIcons[ext];

  const iconToUse =
    iconFilename ||
    iconForExt ||
    "/icons/file-extension-items/seti-ui/default.svg";

  const filePadding = "pl-1";

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(fileData.fullPath);
  };

  if (fileData.type === "file") {
    return (
      <div
        className={cn(
          "flex items-center gap-1 text-2xs group hover:bg-secondary-n-900 rounded-sm",
          filePadding
        )}
        key={fileData.fullPath}
      >
        <img
          alt={ext}
          src={iconToUse}
          width="20"
          height="20"
          style={{
            boxSizing: "initial",
          }}
          className="max-w-none"
        />
        {filename}
        <X
          onClick={handleDelete}
          className="text-icons-primary h-3 w-3 mr-1 ml-auto cursor-pointer hidden group-hover:block"
        />
      </div>
    );
  }
  return (
    <div>
      <div
        onClick={handleToggle}
        className="cursor-pointer flex items-center group text-2xs group hover:bg-secondary-n-900 rounded-sm py-0.5"
      >
        {isExpanded ? (
          <ChevronDown className="mr-2 w-3 h-3" />
        ) : (
          <ChevronRightIcon className="mr-2 w-3 h-3" />
        )}
        {filename}
        <X
          onClick={handleDelete}
          className="text-icons-primary h-3 w-3 ml-auto cursor-pointer hidden group-hover:block mr-1"
        />
      </div>
      {isExpanded && fileData.items && (
        <div className={cn(filePadding)}>
          {fileData.items.map((item, index) => (
            <FileTree key={index} fileData={item} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
});

const deleteFileOrFolder = (
  files: FileData[],
  targetPath: string
): FileData[] => {
  return files
    .filter((file) => file.fullPath !== targetPath)
    .map((file) => {
      if (file.type === "folder" && file.items) {
        return {
          ...file,
          items: deleteFileOrFolder(file.items, targetPath),
        };
      }
      return file;
    });
};

// FileViewer component
const FileViewer = () => {
  const { selectedFiles } = useDesignSystemStore(
    (state) => state.generation.context
  );

  const setSelectedFiles = useDesignSystemStore(
    (state) => state.setSelectedFiles
  );

  const handleDelete = (filePath: string) => {
    const updatedFiles = deleteFileOrFolder(selectedFiles, filePath);
    setSelectedFiles(updatedFiles);
  };

  return (
    <div className="flex flex-col gap-1 max-h-[200px] overflow-y-auto p-2 border rounded-md border-secondary-300">
      {selectedFiles.map((file, index) => (
        <FileTree key={index} fileData={file} onDelete={handleDelete} />
      ))}
    </div>
  );
};

export default React.memo(FileViewer);
